// Phase 85.A.73 — Header icon "Tin nhắn" cho VĐV (Q9a).
//
// v17.perf.messages-ws (02/2026) — WS-first: subscribe kênh `notifications`
// qua wsHub, cập nhật unread badge tức thì khi BE fanout `message.updated`.
// HTTP polling giữ làm fallback (60s) khi WS lạc / mobile kill socket.
//
// v17.perf.har3 (13/08/2026) — HAR PROD sau v17.perf.messages-ws phát hiện
// burst 8 requests trong 15s đầu do useEffect deps [user, onMessagesPage,
// dmChatEnabled] fire nhiều lần trong login flow + StrictMode double-mount.
// Migrate sang React Query với shared queryKey → dedupe cache-level (mọi
// mount đọc cùng cache). Fallback poll 60s giữ qua refetchInterval. WS
// subscribe giữ nguyên (kênh push chính).
import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChatCircle } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { messagesUserApi } from "../../lib/messagesApi";
import { useAuth } from "../../contexts/AuthContext";
import { playTingTing } from "../../lib/messageNotificationSound";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import { subscribe as wsSubscribe } from "../../lib/wsHub";

const POLL_MS = 60_000;
export const MESSAGES_ME_CONVERSATION_QUERY_KEY = ["messages", "me", "conversation"];

export default function UserMessagesIconButton() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const qc = useQueryClient();
  const prevUnreadRef = useRef(0);
  const firstLoadRef = useRef(true);
  const { enabled: dmChatEnabled } = useFeatureFlag("dm_chat");

  const onMessagesPage = location.pathname.startsWith("/athlete/messages");
  const shouldFetch = !loading && !!user && dmChatEnabled;

  // Fetch initial + poll fallback via React Query. Deduped cross-mount.
  const { data: unreadUser } = useQuery({
    queryKey: MESSAGES_ME_CONVERSATION_QUERY_KEY,
    enabled: shouldFetch,
    queryFn: async () => {
      try {
        const r = await messagesUserApi.getConversation();
        return Math.max(0, Number(r?.data?.conversation?.unread_user || 0));
      } catch {
        return 0;
      }
    },
    // Poll 60s làm fallback safety net cho WS. Trên trang chat thì page
    // tự xử lý unread → tắt poll để tiết kiệm.
    refetchInterval: shouldFetch && !onMessagesPage ? POLL_MS : false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  // Ting-ting khi unread tăng (skip lần đầu). Chạy sau khi data update.
  useEffect(() => {
    if (unreadUser === undefined) return;
    const newUnread = Math.max(0, Number(unreadUser || 0));
    if (!firstLoadRef.current && newUnread > prevUnreadRef.current) {
      try { playTingTing(); } catch { /* noop */ }
    }
    firstLoadRef.current = false;
    prevUnreadRef.current = newUnread;
  }, [unreadUser]);

  // WS subscribe — realtime update badge; ghi trực tiếp vào RQ cache để
  // effect tăng ting-ting cũng chạy đúng thứ tự.
  useEffect(() => {
    if (!shouldFetch) return undefined;
    const unsub = wsSubscribe("notifications", (payload) => {
      const t = payload?.type || payload?.data?.type;
      if (t !== "message.updated") return;
      const u = payload?.unread_user ?? payload?.data?.unread_user;
      if (u !== undefined) {
        qc.setQueryData(MESSAGES_ME_CONVERSATION_QUERY_KEY, Math.max(0, Number(u || 0)));
      }
    });
    const forcePoll = () => qc.invalidateQueries({ queryKey: MESSAGES_ME_CONVERSATION_QUERY_KEY });
    window.addEventListener("messages-unread-changed", forcePoll);
    return () => {
      unsub?.();
      window.removeEventListener("messages-unread-changed", forcePoll);
    };
  }, [shouldFetch, qc]);

  const unread = Math.max(0, Number(unreadUser || 0));

  if (loading || !user) return null;
  if (!dmChatEnabled) return null;

  return (
    <Link
      to="/athlete/messages"
      title="Tin nhắn với BTC"
      data-testid="athlete-messages-icon-btn"
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl text-white/70 hover:text-white hover:bg-white/5 transition"
    >
      <ChatCircle size={20} weight="duotone" />
      {unread > 0 ? (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-black px-1 flex items-center justify-center border border-[#0F1114] shadow-[0_2px_6px_rgba(239,68,68,0.6)]"
          data-testid="athlete-messages-unread-badge"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
    </Link>
  );
}
